import React from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';

import { Capability } from '__types__';
import { useRestrictedAccess } from 'lib/restrictedAccess';
import { archiveUserGroup } from 'store/userGroups';
import { displayAlert } from 'store/v1/ui/ui.actions';
import useModal from 'hooks/useModal';

import { Button } from 'shared/buttons';
import { Flyover } from 'v1/components/shared';
import { ConfirmModal, UserGroupModal } from 'modals';

type UserGroupOptionsProps = {
  id: string;
};

const UserGroupOptions = ({ id }: UserGroupOptionsProps) => {
  const dispatch = useDispatch();
  const { openModal } = useModal();

  const hasUpdateCapability = useRestrictedAccess(null, null, [
    Capability.USER_GROUP_UPDATE
  ]);
  const hasArchiveCapability = useRestrictedAccess(null, null, [
    Capability.USER_GROUP_ARCHIVE
  ]);
  const showMenu = hasUpdateCapability || hasArchiveCapability;

  const onEdit = () => openModal(() => <UserGroupModal id={id} />);

  const onArchive = () => {
    openModal(() => (
      <ConfirmModal
        title="Are you sure you want to archive this user group?"
        confirmLabel="Archive user group"
        onConfirm={() => {
          (dispatch(archiveUserGroup(id)) as unknown as Promise<any>)
            .then(unwrapResult)
            .then(() => {
              dispatch(displayAlert('success', 'Updated successfully'));
            })
            .catch(() => {
              dispatch(displayAlert('error', 'An error occurred'));
            });
        }}
      />
    ));
  };

  if (!showMenu) {
    return null;
  }

  return (
    <Flyover>
      <Flyover.Trigger>
        <Button icon="options" size="s" />
      </Flyover.Trigger>
      <Flyover.Content>
        {hasUpdateCapability && (
          <Flyover.Item
            icon="/images/icon_pencil.svg"
            title="Edit user group"
            onClick={onEdit}
          />
        )}
        {hasArchiveCapability && (
          <Flyover.Item
            icon="/images/icon_delete.svg"
            title="Archive user group"
            onClick={onArchive}
          />
        )}
      </Flyover.Content>
    </Flyover>
  );
};

export default UserGroupOptions;
